import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Truck, ShieldCheck, Clock, CheckCircle, WhatsappLogo, ArrowRight, Lightning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { naira } from "@/lib/format";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_diesel-deposit/artifacts/s6104e45_Trucks.webp";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/public/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const wa = settings.whatsapp_number
    ? `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent("Hello RODAIR, I would like to request fuel delivery.")}`
    : "#";

  return (
    <div>
      {/* Hero banner */}
      <section className="relative overflow-hidden">
        <img src={HERO_IMG} alt="RODAIR fuel delivery fleet on the road" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#062B1D]/95 via-[#0A4F35]/85 to-[#0A4F35]/25" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-20 lg:py-32">
          <div className="max-w-2xl rodair-fade">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E5B800] px-3 py-1 text-xs font-bold text-black uppercase tracking-widest">
              Bulk Fuel Supply · Nigeria
            </span>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] drop-shadow-sm">
              Need Diesel or Petrol Delivered?
            </h1>
            <p className="mt-5 text-lg text-emerald-50/90 max-w-xl">
              Reliable bulk fuel supply for businesses and organisations — banks, manufacturers,
              churches and corporate procurement teams. Fast turnaround on every order.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/request-fuel">
                <Button data-testid="hero-request-btn" className="w-full sm:w-auto h-12 px-7 bg-[#E5B800] hover:bg-[#CCA300] text-black font-bold text-base">
                  Request Fuel Delivery <ArrowRight size={20} weight="bold" className="ml-1" />
                </Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer">
                <Button data-testid="hero-whatsapp-btn" variant="outline" className="w-full sm:w-auto h-12 px-7 border-white/80 bg-white/10 text-white font-semibold text-base hover:bg-white/20 backdrop-blur-sm">
                  <WhatsappLogo size={20} weight="fill" className="mr-1" /> WhatsApp Us
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-emerald-50/85">
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-[#E5B800]" /> No registration to request a quote</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-[#E5B800]" /> Secure, professional invoicing</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-[#E5B800]" /> Fast turnaround on delivery</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#0A4F35]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 grid sm:grid-cols-3 gap-8 text-white">
          {[
            { icon: Lightning, t: "Fast Turnaround", d: "Rapid dispatch & delivery — our competitive edge." },
            { icon: Truck, t: "Bulk Delivery", d: "Diesel (AGO) & Petrol (PMS) delivered to your site." },
            { icon: Clock, t: "Payment-First Process", d: "Clear obligations before dispatch — no surprises." },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-3">
              <f.icon size={30} weight="light" className="text-[#E5B800] shrink-0" />
              <div>
                <h3 className="font-display font-semibold">{f.t}</h3>
                <p className="text-sm text-emerald-100/80 mt-1">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Our Products</h2>
            <p className="mt-2 text-slate-600">Indicative prices — final rate agreed per order.</p>
          </div>
          <Link to="/products" className="hidden sm:inline text-sm font-semibold text-[#0A4F35] hover:underline">View all →</Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          {products.map((p) => (
            <div key={p.id} className="border border-[#E5E7EB] rounded-lg p-6 bg-white flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-500">Minimum order {p.min_order_litres?.toLocaleString()} litres</p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-400">From</div>
                <div className="font-display text-2xl font-extrabold text-[#0A4F35]">{naira(p.price_per_litre)}</div>
                <div className="text-xs text-slate-400">per litre</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400">{settings.indicative_note}</p>
      </section>

      {/* How it works CTA */}
      <section className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Fuel delivery, made simple</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Request → Receive Quote → Accept → Pay → Dispatch → Track → Delivered. Payment is part of the
            process that releases your delivery.
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/how-it-works"><Button variant="outline" className="border-[#0A4F35] text-[#0A4F35]">How Delivery Works</Button></Link>
            <Link to="/request-fuel"><Button className="bg-[#0A4F35] hover:bg-[#083D29] text-white">Request Fuel</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
